import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Terms from './pages/Terms';
import Settings from './pages/Settings';
import Onboarding from './pages/Onboarding';
import About from './pages/About';
import { apiUrl } from './lib/api';
const readJsonResponse = async (response) => {
  if (!response || response.status === 204) {
    return null;
  }

  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('Received non-JSON response', error);
    return null;
  }
};

const DEFAULT_PROFILE = { bio: '', title: '', location: '' };
const NOTIFICATION_CHANNELS = ['email', 'push'];
const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const createNotificationDefaults = () => ({
  dueRemindersEnabled: true,
  dueReminderLeadHours: 24,
  dueReminderChannels: ['email'],
  weeklySummaryEnabled: true,
  weeklySummaryChannel: 'email',
  weeklySummaryDay: 'monday',
});
const DEFAULT_SETTINGS = {
  timezone: 'UTC',
  weeklyDigest: true,
  theme: 'nebula',
  notifications: createNotificationDefaults(),
};
const VIEW_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'completed', label: 'Completed' },
];
const PRIORITY_FILTERS = [
  { id: 'all', label: 'Any priority' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
];

const normalizeNotificationSettings = (raw = {}) => {
  const defaults = createNotificationDefaults();
  const lead = Number(raw.dueReminderLeadHours);
  const dedupedChannels = Array.isArray(raw.dueReminderChannels) && raw.dueReminderChannels.length
    ? [...new Set(raw.dueReminderChannels.filter((channel) => NOTIFICATION_CHANNELS.includes(channel)))]
    : defaults.dueReminderChannels;

  return {
    dueRemindersEnabled: raw.dueRemindersEnabled ?? defaults.dueRemindersEnabled,
    dueReminderLeadHours: Number.isFinite(lead) ? lead : defaults.dueReminderLeadHours,
    dueReminderChannels: dedupedChannels,
    weeklySummaryEnabled: raw.weeklySummaryEnabled ?? defaults.weeklySummaryEnabled,
    weeklySummaryChannel: NOTIFICATION_CHANNELS.includes(raw.weeklySummaryChannel)
      ? raw.weeklySummaryChannel
      : defaults.weeklySummaryChannel,
    weeklySummaryDay: WEEK_DAYS.includes(raw.weeklySummaryDay)
      ? raw.weeklySummaryDay
      : defaults.weeklySummaryDay,
  };
};

const normalizeUser = (data) => {
  if (!data) {
    return null;
  }

  return {
    id: data.id || data._id || '',
    email: data.email || '',
    name: data.name || '',
    createdAt: data.createdAt || '',
    profile: {
      bio: data.profile?.bio ?? DEFAULT_PROFILE.bio,
      title: data.profile?.title ?? DEFAULT_PROFILE.title,
      location: data.profile?.location ?? DEFAULT_PROFILE.location,
    },
    settings: {
      timezone: data.settings?.timezone || DEFAULT_SETTINGS.timezone,
      weeklyDigest:
        data.settings?.weeklyDigest === undefined
          ? DEFAULT_SETTINGS.weeklyDigest
          : data.settings.weeklyDigest,
      theme: data.settings?.theme || DEFAULT_SETTINGS.theme,
      notifications: normalizeNotificationSettings(data.settings?.notifications),
    },
  };
};

const readStoredUser = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('user');
    return raw ? normalizeUser(JSON.parse(raw)) : null;
  } catch (error) {
    console.warn('Unable to parse stored user', error);
    return null;
  }
};

const readStoredToken = () => {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem('token') || '';
  } catch (error) {
    console.warn('Unable to read stored token', error);
    return '';
  }
};

const getTodoId = (todo) => (todo && (todo._id || todo.id)) || '';

const formatDueDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString();
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

const isSameDay = (dateA, dateB) => {
  if (!dateA || !dateB) return false;
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

const canModifyTodo = (todo) => todo?.accessLevel === 'owner' || todo?.accessLevel === 'editor';

const onboardingStorageKey = (userId) => (userId ? `pathfinder:onboarding:${userId}` : null);

const readStoredOnboarding = (userId) => {
  if (typeof window === 'undefined') return null;
  const key = onboardingStorageKey(userId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const persistOnboarding = (userId, value) => {
  if (typeof window === 'undefined') return;
  const key = onboardingStorageKey(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    /* ignore */
  }
};

export default function App() {
  const [todos, setTodos] = useState([]);
  const [todoError, setTodoError] = useState('');
  const [authMode, setAuthMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [user, setUser] = useState(() => readStoredUser());
  const [token, setToken] = useState(() => readStoredToken());
  const persistUser = useCallback((data) => {
    if (!data) {
      setUser(null);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('user');
      }
      return null;
    }

    const normalized = normalizeUser(data);
    setUser(normalized);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('user', JSON.stringify(normalized));
    }
    return normalized;
  }, []);
  const [authFeedback, setAuthFeedback] = useState({ message: '', error: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [resetForm, setResetForm] = useState({ email: '', token: '', password: '' });
  const [resetStage, setResetStage] = useState('request');
  const [newTodo, setNewTodo] = useState({
    title: '',
    dueDate: '',
    priority: 'medium',
    tags: '',
    notes: '',
  });
  const [todoLoading, setTodoLoading] = useState(false);
  const [todosLoading, setTodosLoading] = useState(false);
  const [todoActionId, setTodoActionId] = useState('');
  const [editingTodo, setEditingTodo] = useState(null);
  const [viewFilter, setViewFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const authPanelRef = useRef(null);
  const todoPanelRef = useRef(null);
  const [headerCompact, setHeaderCompact] = useState(false);
  const [pathfinderPlan, setPathfinderPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState('');
  const [onboardingState, setOnboardingState] = useState({ data: null, completed: false });
  const [shouldFocusAuth, setShouldFocusAuth] = useState(false);

  useEffect(() => {
    if (user) {
      setAuthMode('login');
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) {
      setOnboardingState({ data: null, completed: false });
      setPathfinderPlan(null);
      return;
    }
    const stored = readStoredOnboarding(user.id) || {};
    setOnboardingState({ data: stored.data || null, completed: Boolean(stored.completed) });
    if (stored.data?.plan) {
      setPathfinderPlan(stored.data.plan);
    }
  }, [user?.id]);

  const onboardingRequired = Boolean(user?.id && !onboardingState.completed);

  useEffect(() => {
    if (!token) {
      setPathfinderPlan(null);
      setPlanLoading(false);
      setPlanError('');
      return;
    }

    let cancelled = false;
    setPlanLoading(true);
    setPlanError('');

    fetch(apiUrl('/api/pathfinder/plan'), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        if (res.status === 404) {
          return null;
        }
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(text || 'Unable to load plan');
        }
        return res.json();
      })
      .then((plan) => {
        if (cancelled) return;
        setPathfinderPlan(plan);
        setPlanError('');
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn('Failed to load Pathfinder plan', error);
        setPlanError('Unable to load your Pathfinder plan right now.');
      })
      .finally(() => {
        if (!cancelled) {
          setPlanLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, onboardingState.completed]);

  const handleLogout = useCallback(
    (message = 'Logged out.') => {
      persistUser(null);
      setToken('');
      setAuthFeedback({ message, error: '' });
      setTodos([]);
      setEditingTodo(null);
      setViewFilter('all');
      setPriorityFilter('all');
      setTagFilter('');
      setOnboardingState({ data: null, completed: false });
      setPathfinderPlan(null);
      setPlanError('');
      setPlanLoading(false);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('token');
        if (user?.id) {
          const key = onboardingStorageKey(user.id);
          if (key) {
            window.localStorage.removeItem(key);
          }
        }
      }
    },
    [persistUser, user?.id]
  );

  const sendAnalytics = useCallback(
    (eventName, meta = {}) => {
      if (!eventName) return;
      const payload = {
        event: eventName,
        meta: { ...meta, path: location.pathname },
      };
      if (user?.id) {
        payload.userId = user.id;
      }
      const body = JSON.stringify(payload);
      const send = () =>
        fetch(apiUrl('/api/analytics'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {});
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        try {
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(apiUrl('/api/analytics'), blob);
          return;
        } catch (error) {
          send();
          return;
        }
      }
      send();
    },
    [location.pathname, user]
  );

  useEffect(() => {
    sendAnalytics('page_view');
  }, [sendAnalytics]);

  useEffect(() => {
    const handleScroll = () => {
      const shouldCompact = window.scrollY > 32;
      setHeaderCompact((current) => (current !== shouldCompact ? shouldCompact : current));
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    fetch(apiUrl('/api/me'), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          throw new Error('unauthorized');
        }
        if (!res.ok) {
          throw new Error('Unable to load profile');
        }
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          persistUser(data);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        if (error.message === 'unauthorized') {
          handleLogout('Session expired. Please log in again.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, persistUser, handleLogout]);

  useEffect(() => {
    if (authMode !== 'reset') {
      setResetStage('request');
      setResetForm({ email: '', token: '', password: '' });
    }
  }, [authMode]);

  const scrollToTodos = () => {
    if (!todoPanelRef.current) return;
    todoPanelRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  };

  const scrollToAuth = useCallback(() => {
    if (!authPanelRef.current) return;
    authPanelRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }, []);

  useEffect(() => {
    if (!shouldFocusAuth) return;
    if (location.pathname !== '/') return;

    const timer = setTimeout(() => {
      scrollToAuth();
      setShouldFocusAuth(false);
    }, 75);

    return () => clearTimeout(timer);
  }, [shouldFocusAuth, location.pathname, scrollToAuth]);

  const filteredTodos = useMemo(() => {
    if (!Array.isArray(todos)) return [];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTomorrow = new Date(startOfToday);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
    const normalizedTagFilter = tagFilter.trim().toLowerCase();

    return todos.filter((todo) => {
      const dueDate = parseDate(todo.dueDate);
      let matchesView = true;

      switch (viewFilter) {
        case 'today':
          matchesView = Boolean(dueDate) && isSameDay(dueDate, now) && !todo.done;
          break;
        case 'upcoming':
          matchesView = Boolean(dueDate) && dueDate >= startOfTomorrow && !todo.done;
          break;
        case 'completed':
          matchesView = Boolean(todo.done);
          break;
        default:
          matchesView = true;
      }

      if (!matchesView) {
        return false;
      }

      const normalizedPriority = (todo.priority || 'medium').toLowerCase();
      if (priorityFilter !== 'all' && normalizedPriority !== priorityFilter) {
        return false;
      }

      if (normalizedTagFilter) {
        const tagPool = Array.isArray(todo.tags) ? todo.tags : todo.tags ? [todo.tags] : [];
        const combinedTags = tagPool.join(',').toLowerCase();
        if (!combinedTags.includes(normalizedTagFilter)) {
          return false;
        }
      }

      return true;
    });
  }, [todos, viewFilter, priorityFilter, tagFilter]);

  const planPreviewSteps = useMemo(() => {
    if (!pathfinderPlan?.steps) return [];
    return pathfinderPlan.steps.slice(0, 3);
  }, [pathfinderPlan]);

  const planToneLabel = useMemo(() => {
    if (!pathfinderPlan?.tone) return 'Supportive';
    return pathfinderPlan.tone.charAt(0).toUpperCase() + pathfinderPlan.tone.slice(1);
  }, [pathfinderPlan?.tone]);

  const planGeneratedLabel = useMemo(() => {
    if (!pathfinderPlan?.generatedAt) return '';
    try {
      return new Date(pathfinderPlan.generatedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return '';
    }
  }, [pathfinderPlan?.generatedAt]);

  const hasActiveFilters =
    viewFilter !== 'all' || priorityFilter !== 'all' || Boolean(tagFilter.trim());
  const canUseTodoControls = Boolean(user && token);

  useEffect(() => {
    if (!token) {
      setTodos([]);
      setTodosLoading(false);
      return;
    }

    setTodosLoading(true);
    fetch(apiUrl('/api/todos'), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Session expired. Please log in again.');
          }
          throw new Error('Unable to load todos');
        }
        return res.json();
      })
      .then(setTodos)
      .catch((error) => {
        setTodoError(error.message);
        if (error.message.includes('Session expired')) {
          handleLogout('Session expired. Please log in again.');
        }
      })
      .finally(() => {
        setTodosLoading(false);
      });
  }, [token, handleLogout]);

  const createTodo = async (payload) => {
    if (!token) {
      setTodoError('You need to be logged in to add todos.');
      return;
    }
    setTodoError('');
    setTodoLoading(true);
    try {
      const res = await fetch(apiUrl('/api/todos'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Failed to create todo');
      }

      const created = await res.json();
      setTodos((current) => [created, ...current]);
      setNewTodo({ title: '', dueDate: '', priority: 'medium', tags: '', notes: '' });
      sendAnalytics('todo_created', { priority: payload.priority });
    } catch (error) {
      setTodoError(error.message);
      sendAnalytics('todo_create_error', { message: error.message });
    } finally {
      setTodoLoading(false);
    }
  };

  const updateTodo = async (id, updates) => {
    if (!token) {
      setTodoError('You need to be logged in to update todos.');
      return;
    }
    if (!id) return;
    setTodoError('');
    setTodoActionId(id);
    try {
      const res = await fetch(apiUrl(`/api/todos/${id}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Todo not found.');
        }
        let errorMessage = 'Unable to update todo.';
        try {
          const body = await res.json();
          if (body?.error) {
            errorMessage = body.error;
          }
        } catch (error) {
          /* ignore json parse errors */
        }
        throw new Error(errorMessage);
      }

      const updated = await res.json();
      setTodos((current) => current.map((todo) => (getTodoId(todo) === id ? updated : todo)));
    } catch (error) {
      setTodoError(error.message);
    } finally {
      setTodoActionId('');
    }
  };

  const deleteTodo = async (id) => {
    if (!token) {
      setTodoError('You need to be logged in to delete todos.');
      return;
    }
    if (!id) return;
    setTodoError('');
    setTodoActionId(id);
    try {
      const res = await fetch(apiUrl(`/api/todos/${id}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Todo not found.');
        }
        let errorMessage = 'Unable to delete todo.';
        try {
          const body = await res.json();
          if (body?.error) {
            errorMessage = body.error;
          }
        } catch (error) {
          /* ignore json parse errors */
        }
        throw new Error(errorMessage);
      }

      setTodos((current) => current.filter((todo) => getTodoId(todo) !== id));
    } catch (error) {
      setTodoError(error.message);
    } finally {
      setTodoActionId('');
    }
  };

  const handleTodoInputChange = (event) => {
    const { name, value } = event.target;
    setNewTodo((prev) => ({ ...prev, [name]: value }));
  };

  const handleTodoSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      setTodoError('You need to be logged in to add todos.');
      return;
    }

    if (!newTodo.title.trim()) {
      setTodoError('Title is required.');
      return;
    }

    const payload = {
      title: newTodo.title.trim(),
      dueDate: newTodo.dueDate || undefined,
      priority: newTodo.priority,
      tags: newTodo.tags
        ? newTodo.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : undefined,
      notes: newTodo.notes.trim() ? newTodo.notes.trim() : undefined,
    };

    await createTodo(payload);
  };

  const handleToggleDone = (todo) => {
    const id = getTodoId(todo);
    if (!id) return;
    if (!canModifyTodo(todo)) {
      setTodoError('You only have viewer access to this shared todo.');
      return;
    }
    updateTodo(id, { done: !todo.done });
  };

  const handleEditTodo = (todo) => {
    const id = getTodoId(todo);
    if (!id) return;
    if (!canModifyTodo(todo)) {
      setTodoError('You only have viewer access to this shared todo.');
      return;
    }
    setTodoError('');
    setEditingTodo({
      id,
      title: todo.title || '',
      dueDate: todo.dueDate ? todo.dueDate.slice(0, 10) : '',
      priority: (todo.priority || 'medium').toLowerCase(),
      tags: Array.isArray(todo.tags) ? todo.tags.join(', ') : todo.tags || '',
      notes: todo.notes || '',
      accessLevel: todo.accessLevel,
    });
  };

  const handleEditFieldChange = (event) => {
    if (!editingTodo) return;
    const { name, value } = event.target;
    setEditingTodo((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelEdit = () => {
    setEditingTodo(null);
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editingTodo) return;

    if (!canModifyTodo(editingTodo)) {
      setTodoError('You only have viewer access to this shared todo.');
      return;
    }

    const trimmedTitle = editingTodo.title.trim();
    if (!trimmedTitle) {
      setTodoError('Title is required.');
      return;
    }

    const payload = {
      title: trimmedTitle,
      dueDate: editingTodo.dueDate || undefined,
      priority: editingTodo.priority || 'medium',
      tags: editingTodo.tags
        ? editingTodo.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : undefined,
      notes: editingTodo.notes.trim() ? editingTodo.notes.trim() : undefined,
    };

    await updateTodo(editingTodo.id, payload);
    setEditingTodo(null);
  };

  const handleDeleteTodo = (todo) => {
    const id = getTodoId(todo);
    if (!id) return;
    if (!canModifyTodo(todo)) {
      setTodoError('You only have viewer access to this shared todo.');
      return;
    }
    const shouldDelete = typeof window === 'undefined' ? true : window.confirm('Delete this todo?');
    if (!shouldDelete) return;
    deleteTodo(id);
  };

  const handleViewFilterChange = (value) => {
    setViewFilter(value);
  };

  const handlePriorityFilterChange = (event) => {
    setPriorityFilter(event.target.value);
  };

  const handleTagFilterChange = (event) => {
    setTagFilter(event.target.value);
  };

  const handleResetFilters = () => {
    setViewFilter('all');
    setPriorityFilter('all');
    setTagFilter('');
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetInputChange = (event) => {
    const { name, value } = event.target;
    setResetForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetRequest = async (event) => {
    event.preventDefault();
    const email = resetForm.email.trim();
    if (!email) {
      setAuthFeedback({ message: '', error: 'Enter the email you use with Pathfinder.' });
      return;
    }

    setAuthLoading(true);
    setAuthFeedback({ message: '', error: '' });

    try {
      const res = await fetch(apiUrl('/api/auth/request-reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const responsePayload = await readJsonResponse(res);
      if (!res.ok) {
        throw new Error(responsePayload?.error || 'Unable to start the reset flow.');
      }

      const baseMessage =
        responsePayload?.message ||
        'If the email is registered, instructions are on the way. Check your inbox.';
      const debugSuffix = responsePayload?.debugToken
        ? ` Temporary code: ${responsePayload.debugToken}`
        : '';

      setAuthFeedback({ message: `${baseMessage}${debugSuffix}`, error: '' });
      setResetStage('confirm');
      setResetForm((prev) => ({ ...prev, token: responsePayload?.debugToken || prev.token }));
      sendAnalytics('auth_reset_requested', {
        delivered: Boolean(responsePayload?.delivered),
        channel: responsePayload?.channel || 'email',
      });
    } catch (error) {
      setAuthFeedback({ message: '', error: error.message });
      sendAnalytics('auth_error', { mode: 'reset_request', message: error.message });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleResetComplete = async (event) => {
    event.preventDefault();
    const email = resetForm.email.trim();
    const tokenValue = resetForm.token.trim();
    const passwordValue = resetForm.password;

    if (!email || !tokenValue || !passwordValue) {
      setAuthFeedback({ message: '', error: 'Provide your email, the reset code, and a new password.' });
      return;
    }

    setAuthLoading(true);
    setAuthFeedback({ message: '', error: '' });

    try {
      const res = await fetch(apiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token: tokenValue, password: passwordValue }),
      });
      const responsePayload = await readJsonResponse(res);
      if (!res.ok) {
        throw new Error(responsePayload?.error || 'Unable to reset the password.');
      }
      if (!responsePayload) {
        throw new Error('Unexpected response from the server.');
      }

      const { token: sessionToken, ...userPayload } = responsePayload;
      const normalizedUser = persistUser(userPayload);
      if (sessionToken) {
        setToken(sessionToken);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('token', sessionToken);
        }
      }

      const onboardingRecord = normalizedUser ? readStoredOnboarding(normalizedUser.id) : null;

      setAuthFeedback({ message: 'Password updated. You are signed in.', error: '' });
      setAuthMode('login');
      setResetStage('request');
      setResetForm({ email: '', token: '', password: '' });
      setForm({ name: '', email: '', password: '' });
      sendAnalytics('auth_success', { mode: 'reset' });

      if (normalizedUser && !onboardingRecord?.completed) {
        navigate('/onboarding');
      }
    } catch (error) {
      setAuthFeedback({ message: '', error: error.message });
      sendAnalytics('auth_error', { mode: 'reset_complete', message: error.message });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    if (authMode === 'register' && !form.name.trim()) {
      setAuthFeedback({ message: '', error: 'Name is required.' });
      return;
    }
    setAuthLoading(true);
    setAuthFeedback({ message: '', error: '' });

    try {
      const endpoint = authMode === 'login' ? 'login' : 'register';
      const payload =
        authMode === 'login'
          ? { email: form.email, password: form.password }
          : { email: form.email, password: form.password, name: form.name.trim() };
      const res = await fetch(apiUrl(`/api/auth/${endpoint}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const responsePayload = await readJsonResponse(res);

      if (!res.ok) {
        throw new Error(
          (responsePayload && responsePayload.error) || `Unable to process request (${res.status})`
        );
      }

      if (!responsePayload) {
        throw new Error('Server returned an unexpected empty response. Please try again.');
      }

      const { token: sessionToken, ...userPayload } = responsePayload;
      const normalizedUser = persistUser(userPayload);

      if (sessionToken) {
        setToken(sessionToken);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('token', sessionToken);
        }
      }

      const onboardingRecord = normalizedUser ? readStoredOnboarding(normalizedUser.id) : null;

      if (authMode === 'login') {
        setAuthFeedback({ message: 'Logged in successfully.', error: '' });
      } else {
        setAuthFeedback({ message: 'Account created. You are logged in.', error: '' });
        setAuthMode('login');
      }

      sendAnalytics('auth_success', { mode: authMode });

      setForm({ name: '', email: '', password: '' });

      if (normalizedUser && !onboardingRecord?.completed) {
        navigate('/onboarding');
      }
    } catch (error) {
      setAuthFeedback({ message: '', error: error.message });
      sendAnalytics('auth_error', { mode: authMode, message: error.message });
    } finally {
      setAuthLoading(false);
    }
  };

  const homeView = (
    <main className="app-shell">
      <section className="auth-panel" ref={authPanelRef}>
        <div className="auth-panel-hero">
          <p className="eyebrow">Concierge workflow engine</p>
          <h1>Navigate complex processes with Pathfinder AI.</h1>
          <p className="helper">
            We interview you like a consultant, generate personalized action playbooks, verify critical
            documents, and adapt the tone of every nudge so stressful journeys feel manageable.
          </p>
          <p className="helper">
            Premium Visa, Business, and Inheritance packs give you clear checklists, quick risk flags, and
            document reviews so you know exactly what to send and when.
          </p>
        </div>
        {!user ? (
          <>
            {authMode !== 'reset' ? (
              <>
                <div className="auth-toggle">
                  <button
                    type="button"
                    className={authMode === 'login' ? 'active' : ''}
                    onClick={() => setAuthMode('login')}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    className={authMode === 'register' ? 'active' : ''}
                    onClick={() => setAuthMode('register')}
                  >
                    Register
                  </button>
                </div>

                <form className="auth-form" onSubmit={handleAuthSubmit}>
                  {authMode === 'register' && (
                    <label>
                      Name
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleInputChange}
                        required
                      />
                    </label>
                  )}
                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleInputChange}
                      autoComplete={authMode === 'login' ? 'username' : 'email'}
                      required
                    />
                  </label>
                  <label>
                    Password
                    <input
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleInputChange}
                      autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                      required
                    />
                  </label>
                  <button type="submit" disabled={authLoading}>
                    {authLoading
                      ? 'Please wait...'
                      : authMode === 'login'
                      ? 'Sign In'
                      : 'Create Account'}
                  </button>
                </form>
                {authMode === 'login' && (
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      setAuthMode('reset');
                      setResetForm((prev) => ({ ...prev, email: form.email }));
                      setAuthFeedback({ message: '', error: '' });
                    }}
                    disabled={authLoading}
                  >
                    Forgot your password?
                  </button>
                )}
              </>
            ) : (
              <div className="reset-panel">
                <p className="eyebrow">Password reset</p>
                <h2>{resetStage === 'request' ? 'Send a reset code' : 'Enter the reset code'}</h2>
                <p className="helper">
                  We send a short-lived code to the email on file. Enter the code here with a new password
                  to get back into your workspace.
                </p>
                {resetStage === 'request' ? (
                  <form className="auth-form" onSubmit={handleResetRequest}>
                    <label>
                      Email
                      <input
                        type="email"
                        name="email"
                        value={resetForm.email}
                        onChange={handleResetInputChange}
                        autoComplete="email"
                        required
                        disabled={authLoading}
                      />
                    </label>
                    <button type="submit" disabled={authLoading}>
                      {authLoading ? 'Sending...' : 'Email me a reset code'}
                    </button>
                  </form>
                ) : (
                  <form className="auth-form" onSubmit={handleResetComplete}>
                    <label>
                      Email
                      <input
                        type="email"
                        name="email"
                        value={resetForm.email}
                        onChange={handleResetInputChange}
                        autoComplete="email"
                        required
                        disabled={authLoading}
                      />
                    </label>
                    <label>
                      Reset code
                      <input
                        type="text"
                        name="token"
                        value={resetForm.token}
                        onChange={handleResetInputChange}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        disabled={authLoading}
                      />
                    </label>
                    <label>
                      New password
                      <input
                        type="password"
                        name="password"
                        value={resetForm.password}
                        onChange={handleResetInputChange}
                        autoComplete="new-password"
                        required
                        disabled={authLoading}
                      />
                    </label>
                    <button type="submit" disabled={authLoading}>
                      {authLoading ? 'Updating...' : 'Update password'}
                    </button>
                  </form>
                )}
                <div className="reset-actions">
                  {resetStage === 'confirm' && (
                    <button
                      type="button"
                      className="auth-link"
                      onClick={() => {
                        setResetStage('request');
                        setResetForm((prev) => ({ ...prev, token: '', password: '' }));
                        setAuthFeedback({ message: '', error: '' });
                      }}
                      disabled={authLoading}
                    >
                      Need to resend the code?
                    </button>
                  )}
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthFeedback({ message: '', error: '' });
                    }}
                    disabled={authLoading}
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="auth-signed-in">
            <p className="signed-in-label">You are signed in.</p>
            <p className="signed-in-identity">
              Welcome back, <span className="signed-in-name-pop">{user.name}</span>
            </p>
            <div className="signed-in-actions">
              <button type="button" className="signed-in-action primary" onClick={scrollToTodos}>
                View playbooks
              </button>
              <Link to="/settings" className="signed-in-action secondary">
                Profile &amp; Settings
              </Link>
              <button
                type="button"
                className="signed-in-action ghost"
                onClick={() => handleLogout()}
              >
                Switch account
              </button>
            </div>
          </div>
        )}

        {authFeedback.message && <p className="status-line success">{authFeedback.message}</p>}
        {authFeedback.error && <p className="status-line error">{authFeedback.error}</p>}
      </section>

      <section className="todo-panel" ref={todoPanelRef}>
        <header>
          <h1>Journey Playbooks</h1>
          <span className="todo-subtitle">
            Every entry expands into steps, documents, and verification cues tuned by Pathfinder AI.
          </span>
        </header>
        {token && planLoading && (
          <p className="status-line info plan-status">Generating your Pathfinder plan…</p>
        )}
        {planError && <p className="status-line error plan-status">{planError}</p>}
        {pathfinderPlan && (
          <section className="plan-panel">
            <div className="plan-panel-header">
              <div>
                <p className="eyebrow">Pathfinder</p>
                <h2>
                  {pathfinderPlan.track
                    ? `${pathfinderPlan.track.charAt(0).toUpperCase()}${pathfinderPlan.track.slice(1)} track`
                    : 'Your Pathfinder plan'}
                </h2>
                <p className="helper">{pathfinderPlan.summary}</p>
              </div>
              <div className="plan-meta-stack">
                <span className="tone-chip">{planToneLabel}</span>
                {planGeneratedLabel && (
                  <span className="plan-generated">Updated {planGeneratedLabel}</span>
                )}
              </div>
            </div>
            <ul className="plan-steps">
              {planPreviewSteps.map((step, index) => {
                const statusLabel = (step.status || 'pending').replace(/_/g, ' ');
                const etaLabel =
                  Number.isFinite(step.etaDays) && step.etaDays > 0
                    ? `${step.etaDays} day${step.etaDays === 1 ? '' : 's'}`
                    : null;
                const tags = Array.isArray(step.tags)
                  ? step.tags.map((tag) => (typeof tag === 'string' ? tag.trim() : '')).filter(Boolean).slice(0, 3)
                  : [];

                return (
                  <li key={step.id || step.title || index} className="plan-step-card">
                    <div className="plan-step-head">
                      <span className="plan-step-index">Step {index + 1}</span>
                      <span className="plan-step-status">{statusLabel}</span>
                    </div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                    <div className="plan-step-meta">
                      {etaLabel && <span>{etaLabel}</span>}
                      {tags.map((tag) => (
                        <span key={tag} className="plan-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="plan-footnote">First two steps are already pinned to your todo list.</p>
          </section>
        )}
        <div className="todo-filters">
          <div className="todo-filters-heading">
            <div>
              <p className="filter-label">Focus view</p>
              <p className="filter-meta">
                {hasActiveFilters ? 'Custom filters active' : 'Showing all todos'}
              </p>
            </div>
            <button
              type="button"
              className="reset-filters"
              onClick={handleResetFilters}
              disabled={!hasActiveFilters || !canUseTodoControls}
            >
              Reset view
            </button>
          </div>
          <div className="todo-view-toggle" role="tablist" aria-label="Todo view filters">
            {VIEW_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`view-chip ${viewFilter === filter.id ? 'active' : ''}`}
                onClick={() => handleViewFilterChange(filter.id)}
                aria-pressed={viewFilter === filter.id}
                disabled={!canUseTodoControls}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="todo-quick-filters">
            <label className="priority-filter">
              Priority
              <select
                value={priorityFilter}
                onChange={handlePriorityFilterChange}
                disabled={!canUseTodoControls}
              >
                {PRIORITY_FILTERS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tag-filter">
              Tags
              <input
                type="text"
                value={tagFilter}
                onChange={handleTagFilterChange}
                placeholder="ops, finance"
                disabled={!canUseTodoControls}
              />
            </label>
          </div>
        </div>
        {token && todosLoading && <p className="status-line info">Loading your playbooks...</p>}
        <form className="todo-form" onSubmit={handleTodoSubmit}>
          <input
            type="text"
            name="title"
            placeholder="Title"
            value={newTodo.title}
            onChange={handleTodoInputChange}
            disabled={!user || !token || todoLoading}
          />
          <div className="todo-grid">
            <label>
              Due date
              <input
                type="date"
                name="dueDate"
                className="date-input"
                value={newTodo.dueDate}
                onChange={handleTodoInputChange}
                disabled={!user || !token || todoLoading}
                aria-label="Due date"
              />
            </label>
            <label>
              Priority
              <select
                name="priority"
                className="priority-select"
                value={newTodo.priority}
                onChange={handleTodoInputChange}
                disabled={!user || !token || todoLoading}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>
          </div>
          <label>
            Tags (comma separated)
            <input
              type="text"
              name="tags"
              value={newTodo.tags}
              onChange={handleTodoInputChange}
              placeholder="work, errands"
              disabled={!user || !token || todoLoading}
            />
          </label>
          <label>
            Notes
            <textarea
              name="notes"
              rows="3"
              value={newTodo.notes}
              onChange={handleTodoInputChange}
              disabled={!user || !token || todoLoading}
            />
          </label>
          <button type="submit" disabled={!user || !token || todoLoading}>
            {todoLoading ? 'Saving...' : 'Add Playbook'}
          </button>
        </form>
        {todoError && <p className="status-line error">{todoError}</p>}
        <ul>
          {filteredTodos.map((t) => {
            const id = getTodoId(t);
            const busy = todoActionId === id;
            const tags = Array.isArray(t.tags)
              ? t.tags
              : t.tags
              ? [t.tags]
              : [];
            const due = formatDueDate(t.dueDate);
            const priority = (t.priority || 'medium').toLowerCase();
            const isEditing = editingTodo?.id === id;
            const isShared = Boolean(t.sharedOwner);
            const sharedOwnerName = t.sharedOwner?.name || t.sharedOwner?.email || 'Shared';
            const sharedAccessLabel =
              t.accessLevel === 'editor' ? 'Editor access' : 'Viewer access';
            const canModify = canModifyTodo(t);
            const viewerMessage = !canModify
              ? 'You only have viewer access to this shared todo.'
              : undefined;
            return (
              <li key={id}>
                {isEditing ? (
                  <form className="todo-edit-form" onSubmit={handleEditSubmit}>
                    <label>
                      Title
                      <input
                        type="text"
                        name="title"
                        value={editingTodo.title}
                        onChange={handleEditFieldChange}
                        required
                      />
                    </label>
                    <div className="todo-edit-grid">
                      <label>
                        Due date
                        <input
                          type="date"
                          name="dueDate"
                          value={editingTodo.dueDate}
                          onChange={handleEditFieldChange}
                        />
                      </label>
                      <label>
                        Priority
                        <select
                          name="priority"
                          value={editingTodo.priority}
                          onChange={handleEditFieldChange}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </label>
                    </div>
                    <label>
                      Tags (comma separated)
                      <input
                        type="text"
                        name="tags"
                        value={editingTodo.tags}
                        onChange={handleEditFieldChange}
                        placeholder="team, ops"
                      />
                    </label>
                    <label>
                      Notes
                      <textarea
                        name="notes"
                        rows={3}
                        value={editingTodo.notes}
                        onChange={handleEditFieldChange}
                      />
                    </label>
                    <div className="todo-edit-actions">
                      <button type="submit" disabled={busy}>
                        {busy ? 'Saving...' : 'Save changes'}
                      </button>
                      <button type="button" onClick={handleCancelEdit} disabled={busy}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="todo-info">
                      <div className="todo-title-row">
                        <span className="todo-title">{t.title}</span>
                        {t.done && <span className="todo-done">Done</span>}
                      </div>
                      <div className="todo-meta">
                        <span className={`priority-badge priority-${priority}`}>{priority}</span>
                        {due && <span>{due}</span>}
                        {tags.length > 0 && <span>{tags.join(', ')}</span>}
                      </div>
                      {isShared && (
                        <span className={`shared-owner-chip ${t.accessLevel}`}>
                          Shared by {sharedOwnerName} · {sharedAccessLabel}
                        </span>
                      )}
                      {t.notes && <p className="todo-notes">{t.notes}</p>}
                    </div>
                    <div className="todo-actions">
                      <button
                        type="button"
                        onClick={() => handleToggleDone(t)}
                        disabled={!user || !token || busy || !canModify}
                        title={viewerMessage}
                      >
                        {t.done ? 'Mark undone' : 'Mark done'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditTodo(t)}
                        disabled={!user || !token || busy || !canModify}
                        title={viewerMessage}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="delete"
                        onClick={() => handleDeleteTodo(t)}
                        disabled={!user || !token || busy || !canModify}
                        title={viewerMessage}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
          {!todosLoading && todos.length === 0 && user && (
            <li className="todo-empty">No playbooks yet. Capture your first mission above.</li>
          )}
          {!todosLoading && todos.length > 0 && filteredTodos.length === 0 && (
            <li className="todo-empty">
              No playbooks match your filters. Try adjusting the view, priority, or tag search.
            </li>
          )}
        </ul>
      </section>
    </main>
  );

  const isAboutPath = location.pathname.startsWith('/about');
  const isTerms = isAboutPath && location.hash === '#terms';
  const isAbout = isAboutPath && !isTerms;
  const isSettings = location.pathname.startsWith('/settings');
  const isOnboarding = location.pathname.startsWith('/onboarding');

  return (
    <div className="site-frame">
      <header className={`site-header ${headerCompact ? 'site-header--compact' : ''}`}>
        <Link to="/" className="site-brand">
          Pathfinder AI
        </Link>
        <nav className="nav-links">
          <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
            Product
          </Link>
          <Link to="/about#about-overview" className={`nav-link ${isAbout ? 'active' : ''}`}>
            About
          </Link>
          {user && !onboardingRequired && (
            <Link to="/settings" className={`nav-link ${isSettings ? 'active' : ''}`}>
              Settings
            </Link>
          )}
          {user && onboardingRequired && (
            <Link to="/onboarding" className={`nav-link ${isOnboarding ? 'active' : ''}`}>
              Onboarding
            </Link>
          )}
          <Link to="/about#terms" className={`nav-link ${isTerms ? 'active' : ''}`}>
            Terms
          </Link>
        </nav>
        <Link
          to="/"
          className="nav-cta"
          onClick={(event) => {
            event.preventDefault();
            setAuthMode('login');
            if (location.pathname === '/') {
              scrollToAuth();
              return;
            }
            setShouldFocusAuth(true);
            navigate('/');
          }}
        >
          Launch Pathfinder
        </Link>
      </header>
      <div className="site-content">
        <Routes>
          <Route path="/" element={homeView} />
          <Route
            path="/settings"
            element={
              token ? (
                onboardingRequired ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <Settings
                    user={user}
                    token={token}
                    onUserUpdate={persistUser}
                    sendAnalytics={sendAnalytics}
                    onLogout={handleLogout}
                  />
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/onboarding"
            element={
              token ? (
                onboardingRequired ? (
                  <Onboarding
                    user={user}
                    token={token}
                    initialData={onboardingState.data}
                    onProgress={(draft) => {
                      if (!user?.id) return;
                      const baseDraft = draft || {};
                      const mergedDraft = {
                        ...baseDraft,
                        ...(baseDraft.plan || onboardingState.data?.plan
                          ? { plan: baseDraft.plan || onboardingState.data?.plan }
                          : {}),
                      };
                      const nextState = { data: mergedDraft, completed: onboardingState.completed };
                      setOnboardingState(nextState);
                      persistOnboarding(user.id, nextState);
                    }}
                    onComplete={(payload) => {
                      if (!user?.id) return;
                      const entry = { data: payload, completed: true };
                      setOnboardingState(entry);
                      persistOnboarding(user.id, entry);
                      if (payload?.plan) {
                        setPathfinderPlan(payload.plan);
                      }
                      navigate('/');
                    }}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}